import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { ApsPermitedScreen } from '../../models/aps-permission/aps-permited-screen';
import { ChoosePlan } from '../../models/choose-plan/choose-plan';
import { ChoosePlanBuyng } from '../../models/choose-plan/choose-plan-buyng';
import { ChoosePlanDuplicatedContract } from '../../models/choose-plan/choose-plan-duplicated-contract';
import { ChoosePlanFilter } from '../../models/choose-plan/choose-plan-filter';
import { ChoosePlanProduct } from '../../models/choose-plan/choose-plan-product';
import { ConstantsFordProtect } from '../../models/constants/constants-ford-protect';
import { PriceName } from '../../models/enum/price-name';
import { SaleChannel } from '../../models/enum/sale-channel';
import { SalesTypeFordEnum } from '../../models/enum/sales-type-ford';
import { StatusChassi } from '../../models/enum/status-chassi';
import { VinValidate } from '../../models/vin-validate/vin-validate';
import { ApsPermissionService } from '../../services/aps-permission/aps-permission.service';
import { ChoosePlanService } from '../../services/choose-plan/choose-plan.service';
import { MessageExceptionService } from '../../services/message-exception/message-exception.service';
import { SystemCountryService } from '../../services/system-country/system-country.service';
import { VehicleInformationService } from '../../services/vehicle-information/vehicle-information.service';


export class PlanFordProtect {
	planSelectedEnum: SelectPlan = SelectPlan.WARRANTY;
}

export enum SelectPlan {
	TOOL_PIECE,
	TOOL,
	WARRANTY
}

@Component({
	selector: 'app-choose-plan',
	templateUrl: './choose-plan.component.html',
	styleUrls: ['./choose-plan.component.scss'],
	standalone: false
})
export class ChoosePlanComponent implements OnInit {
	private readonly destroyRef = inject(DestroyRef);

	planFordProtect: PlanFordProtect = new PlanFordProtect();
	vinValidate: VinValidate = null!;
	choosePlanFilter: ChoosePlanFilter;
	choosePlan: ChoosePlan;
	choosePlanBuyng: ChoosePlanBuyng;
	choosePlanDuplicatedContract: ChoosePlanDuplicatedContract;
	saleChannel: SaleChannel;
	unityPrice = ConstantsFordProtect.PESO;
	editExtensionAction = false;
	editMaintenanceAction = false;
	editPartAction = false;
	productPolicyId: number = 0;
	systemCountry: string = '';
	messageForPriceNoMinimum = false;
	messageForPriceNoMaxAccepted = false;
	maxPermitted: number = 0;
	minPermitted: number = 0;
	selectedProduct = true;
	excludingItemFromSellerPriceExtencion: ChoosePlanProduct[] = [];
	excludingDifferentToSellerExtencion: ChoosePlanProduct[] = [];
	excludingItemFromSellerPriceMaintenance: ChoosePlanProduct[] = [];
	excludingItemFromSellerPricePart: ChoosePlanProduct[] = [];
	selectingExtendedWarranty = false;
	selectingPreOwnedReviews = false;
	selectingOverhaulsWearParts = false;
	reviewContractExists = false;
	extensionSelected: ChoosePlanProduct | null = null;
	maintenanceSelected: ChoosePlanProduct | null = null;
	partSelected: ChoosePlanProduct | null = null;
	isPlansLoading = false;
	hasLoadedPlans = false;

	get isCtaSuccess(): boolean {
		return !this.selectedProduct && !this.isPlansLoading;
	}

	constructor(
		private choosePlanService: ChoosePlanService,
		private router: Router,
		private route: ActivatedRoute,
		private apsPermissionService: ApsPermissionService,
		private translateService: TranslateService,
		private messageService: MessageService,
		private vehicleInformationService: VehicleInformationService,
		private messageExceptionService: MessageExceptionService,
		private systemCountryService: SystemCountryService
	) {

		this.choosePlan = new ChoosePlan();
		this.choosePlanFilter = new ChoosePlanFilter();
		this.choosePlanBuyng = new ChoosePlanBuyng();
		this.choosePlanDuplicatedContract = new ChoosePlanDuplicatedContract();
		this.saleChannel = route.snapshot.paramMap.get('saleChannel') as SaleChannel;
		this.systemCountryService.setSystemCountry();
	}


	ngOnInit() {

		this.systemCountry = this.systemCountryService.getSystemCountry();
		this.vinValidate = this.vehicleInformationService.getAllChassiValidation();

		if (this.vinValidate === null || this.vinValidate.validated === StatusChassi.invalid) {
			this.router.navigate(['sales-channel', this.saleChannel], { skipLocationChange: true, replaceUrl: false });
		}

		this.apsPermissionService.permissionValidation(ApsPermitedScreen.screenPlansPolicy);
		this.getChoosePlanList();
		this.clearExtensionWarranty();

		if (this.choosePlanService.getChoosePlanBuyng() !== null) {
			this.choosePlanBuyng = this.choosePlanService.getChoosePlanBuyng();
		}

		if (this.vinValidate === null || this.vinValidate.vin !== '') {
			this.getChoosePlanDuplicatedContract(this.vinValidate.vin);
		}

		this.planFordProtect.planSelectedEnum = SelectPlan.WARRANTY;

		this.selectingOverhaulsWearParts = this.systemCountry === ConstantsFordProtect.ARG;

		this.clearFieldsButton();
	}

	clearExtensionWarranty() {
		this.choosePlanBuyng = new ChoosePlanBuyng();
	}

	addExtensionWarranty(extension: ChoosePlanProduct) {

		this.choosePlanBuyng.extension = extension;
		this.selectedProduct = false;

		if (this.systemCountry === ConstantsFordProtect.BRA || this.systemCountry === ConstantsFordProtect.ARG) {

			this.selectingOverhaulsWearParts = true;
			this.selectingPreOwnedReviews = true;
		}
	}

	addMaintenance(maintenance: ChoosePlanProduct) {

		this.choosePlanBuyng.maintenance = maintenance;
		this.selectedProduct = false;

		if (this.systemCountry === ConstantsFordProtect.BRA || this.systemCountry === ConstantsFordProtect.ARG) {

			this.selectingExtendedWarranty = true;
			this.selectingOverhaulsWearParts = true;
		}
	}

	addPart(part: ChoosePlanProduct) {

		this.choosePlanBuyng.part = part;
		this.selectedProduct = false;

		if (this.systemCountry === ConstantsFordProtect.BRA || this.systemCountry === ConstantsFordProtect.ARG) {

			this.selectingExtendedWarranty = true;
			this.selectingPreOwnedReviews = true;
		}
	}

	calculateTotalPrice(extension: number, maintenance: number, part: number): number {

		this.choosePlanBuyng.totalPrice = extension + maintenance + part;
		return this.choosePlanBuyng.totalPrice;
	}

	get tool() {
		return SelectPlan.TOOL;
	}

	get toolPiece() {
		return SelectPlan.TOOL_PIECE;
	}

	get warranty() {
		return SelectPlan.WARRANTY;
	}

	onClickPlan(planSelectedEnum: SelectPlan) {
		switch (planSelectedEnum) {

			case SelectPlan.TOOL:

				this.planFordProtect.planSelectedEnum = SelectPlan.TOOL;
				if (this.choosePlanDuplicatedContract.disabledManutencao === true) {

					this.messageService.add({
						severity: 'warn',
						summary: this.translateService.instant('TRANSLATE.ATTENTION'),
						detail: this.translateService.instant('TRANSLATE.COSTUMER.MAINTENANCE.CONTRACT')
					});
				}
				break;

			case SelectPlan.TOOL_PIECE:

				this.planFordProtect.planSelectedEnum = SelectPlan.TOOL_PIECE;

				if (this.reviewContractExists) {

					this.messageService.add({
						severity: 'warn',
						summary: this.translateService.instant('TRANSLATE.ATTENTION'),
						detail: this.translateService.instant('TRANSLATE.COSTUMER.MAINTENANCE.CONTRACT')
					});
				}
				if (this.choosePlanDuplicatedContract.disabledReparo === true) {

					this.messageService.add({
						severity: 'warn',
						summary: this.translateService.instant('TRANSLATE.ATTENTION'),
						detail: this.translateService.instant('TRANSLATE.COSTUMER.WEAR.CONTRACT')
					});
				}
				break;

			case SelectPlan.WARRANTY:

				this.planFordProtect.planSelectedEnum = SelectPlan.WARRANTY;

				if (this.choosePlanDuplicatedContract.disabledExtensao === true) {
					this.messageService.add({
						severity: 'warn',
						summary: this.translateService.instant('TRANSLATE.ATTENTION'),
						detail: this.translateService.instant('TRANSLATE.COSTUMER.EXTENSION.CONTRACT')
					});
				}
				break;

			default:
				break;
		}
	}

	buttonComeBack() {
		this.router.navigate(['vehicle-information', this.saleChannel], { skipLocationChange: true, replaceUrl: false });
	}

	onPriceInputKeydown(event: KeyboardEvent): void {
		const allowedKeys = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];

		if (allowedKeys.includes(event.key) || event.ctrlKey || event.metaKey) {
			return;
		}

		if (!/^[0-9]$/.test(event.key)) {
			event.preventDefault();
		}
	}

	onPriceInputPaste(event: ClipboardEvent, row: ChoosePlanProduct): void {
		const pastedText = event.clipboardData?.getData('text') || '';
		const onlyDigits = pastedText.replace(/\D/g, '');

		event.preventDefault();
		this.formatNumber(onlyDigits, row);
	}

	onPriceInputBlur(row: ChoosePlanProduct): void {
		const value = (row.priceFormatted || '').trim();

		if (!value) {
			row.priceFormatted = this.formatForInput(row.itemPrice || row.originalValue || 0);
			return;
		}

		this.formatNumber(value, row);
	}

	private getPriceBounds(row: ChoosePlanProduct): { min: number; max: number } {
		const suggestedValue = row.suggestedValue ?? row.itemPrice ?? 0;

		if (this.systemCountry === ConstantsFordProtect.BRA) {
			return {
				min: 0,
				max: suggestedValue + (suggestedValue * 0.4)
			};
		}

		if (this.systemCountry === ConstantsFordProtect.ARG) {
			return {
				min: suggestedValue - (suggestedValue * 0.52),
				max: suggestedValue + (suggestedValue * 0.5)
			};
		}

		return {
			min: 0,
			max: Number.MAX_SAFE_INTEGER
		};
	}

	getCurrencyCodeByCountry(): string {
		switch (this.systemCountry) {
			case ConstantsFordProtect.ARG:
				return 'ARS';
			case ConstantsFordProtect.CHL:
				return 'CLP';
			case ConstantsFordProtect.COL:
				return 'COP';
			default:
				return 'BRL';
		}
	}

	getCurrencyDisplayByCountry(): string {
		switch (this.systemCountry) {
			case ConstantsFordProtect.ARG:
			case ConstantsFordProtect.CHL:
			case ConstantsFordProtect.COL:
				return '$ ';
			default:
				return 'symbol';
		}
	}

	getLocaleByCountry(): string {
		switch (this.systemCountry) {
			case ConstantsFordProtect.ARG:
			case ConstantsFordProtect.CHL:
			case ConstantsFordProtect.COL:
				return 'en-US';
			default:
				return ConstantsFordProtect.PT_BR;
		}
	}

	private formatCurrencyValue(value: number): string {
		if ([ConstantsFordProtect.ARG, ConstantsFordProtect.CHL, ConstantsFordProtect.COL].includes(this.systemCountry)) {
			return `${this.getCurrencyDisplayByCountry()}${new Intl.NumberFormat(this.getLocaleByCountry(), {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2
			}).format(value)}`;
		}

		return new Intl.NumberFormat(this.getLocaleByCountry(), {
			style: 'currency',
			currency: this.getCurrencyCodeByCountry(),
			minimumFractionDigits: 2,
			maximumFractionDigits: 2
		}).format(value);
	}

	getPriceRangeHint(row: ChoosePlanProduct): string {
		const { min, max } = this.getPriceBounds(row);
		return this.translateService.instant('TRANSLATE.CHOOSE.PLAN.PRICE.RANGE.HINT', {
			min: this.formatCurrencyValue(min),
			max: this.formatCurrencyValue(max)
		});
	}

	isPriceOutOfBounds(row: ChoosePlanProduct): boolean {
		const value = this.convertToNumber(row.priceFormatted);
		const { min, max } = this.getPriceBounds(row);

		return value < min || value > max;
	}


	isBelowMinimum(row: ChoosePlanProduct): boolean {
		const value = this.convertToNumber(row.priceFormatted);
		const { min } = this.getPriceBounds(row);
		return value < min;
	}

	isAboveMaximum(row: ChoosePlanProduct): boolean {
		const value = this.convertToNumber(row.priceFormatted);
		const { max } = this.getPriceBounds(row);
		return value > max;
	}

	canSaveRow(row: ChoosePlanProduct): boolean {
		if (row.isSaving) {
			return false;
		}

		if (!row.priceFormatted || !row.priceFormatted.trim()) {
			return false;
		}

		return !this.isPriceOutOfBounds(row);
	}

	formatNumber(event: string, row: ChoosePlanProduct) {

		let cleaned = event.replace(/\D/g, "");
		if (cleaned.length > 3) {
			cleaned = cleaned.padStart(3, '0');
		}

		let formatted = cleaned.replace(/(\d+)(\d{2})$/, "$1,$2");
		formatted = formatted.replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1.");
		row.priceFormatted = formatted;
		const numeric = parseFloat(formatted.replace(/\./g, '').replace(',', '.'));
		row.itemPrice = isNaN(numeric) ? 0 : numeric;
	}

	formatForInput(value: number): string {

		const parts = value.toFixed(2).split('.');
		const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
		const decimalPart = parts[1] ? ',' + parts[1] : '';
		return integerPart + decimalPart;
	}

	convertToNumber(valueFormatted: string): number {

		if (valueFormatted === null || valueFormatted === '') {
			return 0;
		}
		const cleaned = valueFormatted.replace(/\./g, '').replace(',', '.');
		const numeric = parseFloat(cleaned);
		return isNaN(numeric) ? 0 : numeric;
	}

	editExtension(row: ChoosePlanProduct) {

		if (this.editExtensionAction) {
			return;
		}

		// Extensão de Garantia
		this.choosePlan.extensionList.forEach(element => {
			element.editing = false;
		});

		this.editExtensionAction = true;
		this.editingFinalPrice(row);
	}

	editMaintenance(row: ChoosePlanProduct) {

		if (this.editMaintenanceAction) {
			return;
		}

		// Revisões Pré-Adquiridas 
		this.choosePlan.maintenanceList.forEach(element => {
			element.editing = false;
		});

		this.editMaintenanceAction = true;
		this.editingFinalPrice(row);
	}

	editPart(row: ChoosePlanProduct) {

		if (this.editPartAction) {
			return;
		}

		// Revisões + peças de desgaste
		this.choosePlan.partList.forEach(element => {
			element.editing = false;
		});

		this.editPartAction = true;
		this.editingFinalPrice(row);
	}

	editingFinalPrice(row: ChoosePlanProduct) {

		if (row.suggestedValue === null || row.suggestedValue === undefined) {

			row.suggestedValue = row.itemPrice;
		}

		row.editing = true;
		row.originalValue = row.itemPrice;
		this.productPolicyId = row.productPolicyKey;
		row.priceFormatted = this.formatForInput(row.itemPrice);
		this.clearChangedPrice();

		setTimeout(() => {
			const input = document.querySelector<HTMLInputElement>('input.input-edit');
			if (input) { input.focus(); }
		}, 60);
	}

	cancelMaintenance(row: ChoosePlanProduct) {

		if (this.editExtensionAction) {
			this.editExtensionAction = false;
		}
		if (this.editMaintenanceAction) {

			this.editMaintenanceAction = false;
		}
		if (this.editPartAction) {

			this.editPartAction = false;
		}

		row.editing = false;
		row.itemPrice = row.originalValue;
		this.clearChangedPrice();
		row.originalValue = 0;
		row.priceFormatted = '';
	}

	saveMaintenance(row: ChoosePlanProduct) {

		if (row.isSaving) {
			return;
		}

		row.isSaving = true;

		this.clearChangedPrice();

		if (this.systemCountry === ConstantsFordProtect.BRA) {

			this.maxPermitted = row.suggestedValue + (row.suggestedValue * 0.4);
			if (row.itemPrice < 0) {
				this.messageForPriceNoMinimum = true;
				row.isSaving = false;
				return;
			}
			if (row.itemPrice > this.maxPermitted) {
				this.messageForPriceNoMaxAccepted = true;
				row.isSaving = false;
				return;
			}
		}

		if (this.systemCountry === ConstantsFordProtect.ARG) {

			this.minPermitted = row.suggestedValue - (row.suggestedValue * 0.52);

			this.maxPermitted = row.suggestedValue + (row.suggestedValue * 0.5);

			if (row.itemPrice < this.minPermitted) {
				this.messageForPriceNoMinimum = true;
				row.isSaving = false;
				return;
			}
			if (row.itemPrice > this.maxPermitted) {
				this.messageForPriceNoMaxAccepted = true;
				row.isSaving = false;
				return;
			}
		}

		if (this.editExtensionAction) {

			this.editExtensionAction = false;
		}
		if (this.editMaintenanceAction) {

			this.editMaintenanceAction = false;
		}
		if (this.editPartAction) {

			this.editPartAction = false;
		}

		row.editing = false;
		row.itemPrice = this.convertToNumber(row.priceFormatted);
		row.originalValue = 0;
		row.priceFormatted = '';
		row.isSaving = false;

		// Trigger button focus by toggling selectedProduct state
		setTimeout(() => {
			this.selectedProduct = true;
			setTimeout(() => {
				this.selectedProduct = false;
			}, 50);
		}, 200);
	}

	clearChangedPrice() {

		this.messageForPriceNoMinimum = false;
		this.messageForPriceNoMaxAccepted = false;
	}

	buttonClean() {

		this.choosePlanBuyng = new ChoosePlanBuyng();
		this.selectedProduct = true;
		this.selectingExtendedWarranty = false;

		if (this.systemCountry === ConstantsFordProtect.BRA || this.systemCountry === ConstantsFordProtect.ARG) {

			this.selectingPreOwnedReviews = false;
		}

		this.selectingOverhaulsWearParts = this.systemCountry === ConstantsFordProtect.BRA ? false : true;

		this.extensionSelected = null;
		this.maintenanceSelected = null;
		this.partSelected = null;
	}

	clearFieldsButton() {
		this.choosePlanBuyng = new ChoosePlanBuyng();
		this.selectedProduct = true;
		this.extensionSelected = null;
		this.maintenanceSelected = null;
		this.partSelected = null;
	}

	buttonNext() {
		this.choosePlanBuyng.factoryDistanceLimit = this.choosePlan.factoryDistanceLimit;
		this.choosePlanBuyng.factoryTimeLimit = this.choosePlan.factoryTimeLimit;
		this.choosePlanService.setChoosePlanBuyng(this.choosePlanBuyng);
		this.router.navigate(['client-data', this.saleChannel], { skipLocationChange: true, replaceUrl: false });
	}

	getChoosePlanList() {
		this.isPlansLoading = true;
		this.hasLoadedPlans = false;
		this.excludingItemFromSellerPriceExtencion = [];
		this.excludingDifferentToSellerExtencion = [];
		this.excludingItemFromSellerPriceMaintenance = [];
		this.excludingItemFromSellerPricePart = [];

		if (this.choosePlanFilter !== null) {

			this.choosePlanFilter.country = this.vinValidate.country;
			this.choosePlanFilter.modelYear = this.vinValidate.modelYear;
			this.choosePlanFilter.tma = this.vinValidate.tma;
			this.choosePlanFilter.vehicleLine = this.vinValidate.vehicleLine;
			this.choosePlanFilter.warrantyStartDate = this.vinValidate.warrantyStartDate;
			this.choosePlanFilter.version = this.vinValidate.version;

			if (this.choosePlanFilter.warrantyStartDate === null) {
				this.choosePlanFilter.warrantyStartDate = new Date();
			}
		}

		if (this.saleChannel === SaleChannel.EXTERNAL) {
			this.choosePlanFilter.saleType = SalesTypeFordEnum.TELEMARKETING_CODE; //Telemarketing
		} else {
			this.choosePlanFilter.saleType = SalesTypeFordEnum.RETAIL_CODE; //dealer ou Varejo
		}

		this.choosePlanService.getChoosePlanProductList(this.choosePlanFilter)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((response: ChoosePlan) => {
				this.isPlansLoading = false;
				this.hasLoadedPlans = true;

				this.choosePlan = response;

				if (this.choosePlan && this.choosePlan.extensionList) {

					this.excludingItemFromSellerPriceExtencion = this.choosePlan.extensionList.filter(extencion => {
						return extencion.priceName !== PriceName.SELLER_PRICE
					});

					this.excludingDifferentToSellerExtencion = this.choosePlan.extensionList.filter(extencion => {
						return extencion.priceName === PriceName.SELLER_PRICE
					});

				}

				if (this.choosePlan && this.choosePlan.maintenanceList) {

					this.excludingItemFromSellerPriceMaintenance = this.choosePlan.maintenanceList.filter(maintenance => {
						return maintenance.priceName !== PriceName.SELLER_PRICE
					});

				}

				if (this.choosePlan && this.choosePlan.partList) {

					this.excludingItemFromSellerPricePart = this.choosePlan.partList.filter(part => {
						return part.priceName !== PriceName.SELLER_PRICE
					});
				}

				if (this.excludingItemFromSellerPriceExtencion.length > 0 &&
					this.excludingItemFromSellerPriceExtencion[0].unityPrice !== null) {
					this.unityPrice = this.excludingItemFromSellerPriceExtencion[0].unityPrice;
				}

				if (this.choosePlan.factoryDistanceLimit === null || this.choosePlan.factoryDistanceLimit === 0) {

					this.messageService.add({
						severity: 'error',
						summary: this.translateService.instant('TRANSLATE.ERROR'),
						detail: this.translateService.instant('TRANSLATE.NO.RECORD.PLANS')
					});
				}

			}, (err) => {
				this.isPlansLoading = false;
				this.hasLoadedPlans = true;

				if (err.status === 404) {
					this.messageService.add({
						severity: 'error',
						summary: this.translateService.instant('TRANSLATE.ERROR'),
						detail: this.translateService.instant('TRANSLATE.VEHICLE.NO.PLANS.AVAILABLE')
					});
				} else if (err.status === 401) {

					this.messageService.add({
						severity: 'error',
						summary: this.translateService.instant('TRANSLATE.ERROR'),
						detail: this.translateService.instant('TRANSLATE.USER.WITHOUT.PERMISSION.REFRESH.PAGE')
					});
				} else {

					this.messageService.add({
						severity: 'error',
						summary: this.translateService.instant('TRANSLATE.ERROR'),
						detail: this.translateService.instant('TRANSLATE.ERROR.CONTACT.SYSTEM.ADMINISTRATOR')
					});
				}

			});
	}

	private getChoosePlanDuplicatedContract(vin: string) {
		this.choosePlanService.getChoosePlanDuplicatedContract(vin)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((response: ChoosePlanDuplicatedContract) => {

				this.choosePlanDuplicatedContract = response;

				if (this.systemCountry === ConstantsFordProtect.BRA &&
					this.choosePlanDuplicatedContract.disabledManutencao === true) {

					this.reviewContractExists = true;
				}

			}, (err) => {
				this.messageExceptionService.getMessageException(
					err.status,
					this.translateService.instant('TRANSLATE.VEHICLE.NOT.ACCESS.LOGIN.AGAIN'));
			});
	}

}
